import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Droplets,
  Wind,
  Mountain,
  Factory,
  AudioLines,
  Waves,
  ArrowRight,
  Mail,
} from "lucide-react";

const heroImage =
  "/Images/services/enviromental/laboratory/analisis-laboratorio.webp";

const experienceImage =
  "/Images/services/enviromental/laboratory/labamb_011.webp";

const monitoreo = [
  {
    title: "Agua",
    icon: Droplets,
  },
  {
    title: "Aire",
    icon: Wind,
  },
  {
    title: "Ruido",
    icon: AudioLines,
  },
  {
    title: "Suelos",
    icon: Mountain,
  },
  {
    title: "Fuentes fijas",
    icon: Factory,
  },
  {
    title: "Hidrobiología",
    icon: Waves,
  },
];

const services = [
  {
    title: "Calidad de Agua",
    icon: Droplets,
    image: "/Images/services/enviromental/laboratory/calidad-agua.webp",
    description:
      "Evaluación físico-química y microbiológica mediante metodologías instrumentales avanzadas.",
    items: [
      "Electrometría",
      "Combustión",
      "Volumetría",
      "Nefelometría",
      "Absorción atómica",
      "Espectrofotometría",
      "ICP",
      "Gravimetría",
    ],
  },

  {
    title: "Calidad de Aire",
    icon: Wind,
    image: "/Images/services/enviromental/laboratory/aire.webp",
    description:
      "Monitoreo y evaluación de contaminantes atmosféricos con técnicas especializadas.",
    items: [
      "Microscopía óptica",
      "Identificación taxonómica",
      "Índices biológicos",
      "Análisis estadístico",
    ],
  },

  {
    title: "Ruido",
    icon: AudioLines,
    image: "/Images/services/enviromental/laboratory/ruido.webp",
    description:
      "Evaluación acústica ambiental mediante mediciones y modelaciones técnicas.",
    items: [
      "Ruido ambiental",
      "Mapas de ruido",
      "Modelaciones",
      "Informes técnicos",
    ],
  },

  {
    title: "Suelos",
    icon: Mountain,
    image: "/Images/services/enviromental/laboratory/suelos.webp",
    description:
      "Caracterización fisicoquímica de suelos mediante análisis instrumentales.",
    items: [
      "Volumetría",
      "Bouyoucos",
      "Absorción atómica",
      "Gravimetría",
      "Cromatografía",
    ],
  },

  {
    title: "Fuentes Fijas",
    icon: Factory,
    image: "/Images/services/enviromental/laboratory/fuentes-fijas.webp",
    description: "Monitoreo y análisis de emisiones atmosféricas industriales.",
    items: [
      "Espectrofotometría UV-Vis",
      "Absorción atómica",
      "Análisis gravimétrico",
    ],
  },

  {
    title: "Hidrobiología",
    icon: Waves,
    image: "/Images/services/enviromental/laboratory/hidrobiologia.webp",
    description:
      "Evaluación biológica de ecosistemas acuáticos mediante análisis especializados.",
    items: [
      "Microscopía óptica",
      "Identificación taxonómica",
      "Índices biológicos",
      "Interpretación de resultados",
    ],
  },
];

const Laboratory = () => {
  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div className="relative h-[380px] sm:h-[420px] md:h-[520px] overflow-hidden">
          <img
            src={heroImage}
            alt="Monitoreo Ambiental"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />

          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-3xl">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#ee7d32]/20 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-wider">
                  Servicios
                </span>

                <h1 className="mt-4 sm:mt-6 text-3xl sm:text-4xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                  Análisis de <br />
                  <span className="text-[#ee7d32]">Laboratorio</span>
                </h1>

                <p className="mt-4 sm:mt-6 text-white/85 text-sm sm:text-base md:text-lg leading-6 sm:leading-7 md:leading-8 max-w-2xl">
                  Soluciones confiables para el control, cumplimiento y
                  sostenibilidad ambiental
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCIA TECNICA */}
      <section className="relative py-16 md:py-24 bg-[#f3f3f3] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative">
              <div className="overflow-hidden rounded-[38px] rounded-tr-[90px] rounded-bl-[90px] shadow-[0_15px_50px_rgba(0,0,0,0.08)]">
                <img
                  src={experienceImage}
                  alt="Experiencia técnica"
                  className="w-full h-[300px] sm:h-[400px] md:h-[500px] object-cover scale-[1.02] hover:scale-[1.05] transition-transform duration-700"
                />
              </div>
            </div>

            <div>
              <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#111] leading-tight">
                Experiencia técnica que <br />
                respalda sus <span className="text-[#ee7d32]">resultados</span>
              </h2>

              <p className="mt-6 text-[#333] text-sm sm:text-base md:text-lg leading-7">
                La sostenibilidad y el cumplimiento ambiental exigen monitoreos
                confiables en agua, aire, ruido, suelos e hidrobiología para
                prevenir impactos, cumplir la normatividad y fortalecer la
                gestión ambiental.
              </p>

              <div className="mt-8 bg-[#e7e7e7] rounded-[26px] p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="flex-shrink-0">
                    <div className="items-center justify-center">
                      <FlaskConical className="w-10 h-10" color="#ee7d32" />
                    </div>
                  </div>

                  <div>
                    <p className="text-sm sm:text-base md:text-lg leading-7 text-[#222]">
                      <span className="font-bold text-[#ee7d32]">
                        Grupo Sierra Alta S.A.S.
                      </span>{" "}
                      combina tecnología y experiencia técnica para entregar
                      resultados precisos y confiables, facilitando la toma de
                      decisiones, el cumplimiento normativo y la protección de
                      los recursos naturales.
                      <br />
                      <br />
                      Nuestros servicios fortalecen la sostenibilidad y la
                      gestión ambiental de su organización.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MONITOREO AMBIENTAL */}
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#111] leading-tight">
              Monitoreo de{" "}
              <span className="text-[#ee7d32]">matrices ambientales</span>
            </h2>

            <p className="mt-5 text-sm sm:text-base md:text-lg text-[#444] leading-7">
              Evaluación y control ambiental en diferentes componentes.
            </p>
          </div>

          <div className="mt-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              {monitoreo.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="group rounded-[24px] border border-[#ececec] bg-[#f8f8f8] px-4 py-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_10px_35px_rgba(0,0,0,0.06)]"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#ee7d32]/10 flex items-center justify-center mb-4 transition-all duration-300">
                      <Icon
                        className="w-7 h-7 text-[#ee7d32] group-hover:text-white transition-all duration-300"
                        color="#ee7d32"
                      />
                    </div>

                    <h3 className="text-sm md:text-base font-bold text-[#111] leading-snug">
                      {item.title}
                    </h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TECNICAS DE ANALISIS */}
      <section className="relative py-32 overflow-hidden bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="text-4xl md:text-5xl font-bold text-[#111] leading-tight">
              Técnicas de <span className="text-[#ee7d32]">análisis</span>
            </h2>

            <p className="mt-5 text-lg text-[#555] leading-7">
              Parámetros monitoreados y metodologías aplicadas en las diferentes
              matrices ambientales.
            </p>
          </div>

          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className={`relative ${index !== 0 ? "mt-36" : ""}`}
              >
                <div
                  className={`grid lg:grid-cols-2 gap-16 lg:gap-24 items-center ${
                    index % 2 !== 0 ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="relative order-2 lg:order-none">
                    <div className="absolute -inset-5 rounded-[40%] bg-[#ee7d32]/10 blur-2xl opacity-70" />

                    <div className="relative group">
                      <div className="absolute inset-0 rounded-[40px] rotate-[-2deg] bg-[#ee7d32] transition duration-500 group-hover:rotate-0" />

                      <div className="relative overflow-hidden rounded-[38px]">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[320px] sm:h-[450px] md:h-[620px] object-cover transition duration-700 group-hover:scale-105"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                        <div className="absolute bottom-8 left-8">
                          <div className="bg-white/85 backdrop-blur-xl px-6 py-4 rounded-[24px] shadow-2xl">
                            <p className="text-xs uppercase tracking-[0.25em] text-[#888]">
                              Matriz
                            </p>

                            <div className="flex items-center gap-2 mt-1">
                              <Icon size={18} className="text-[#ee7d32]" />

                              <h3 className="text-2xl font-bold text-[#111]">
                                {service.title}
                              </h3>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative order-1 lg:order-none">
                    <div className="relative">
                      <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#ee7d32]/10 text-[#ee7d32] text-sm font-semibold">
                        Técnicas especializadas
                      </span>

                      <div className="flex items-center gap-4 mt-7">
                        <div className="w-14 h-14 rounded-2xl bg-[#ee7d32]/10 flex items-center justify-center">
                          <Icon size={28} color="#ee7d32" />
                        </div>

                        <h3 className="text-4xl md:text-5xl font-bold text-[#111] leading-tight">
                          {service.title}
                        </h3>
                      </div>

                      <p className="mt-7 text-lg text-[#666] leading-8 max-w-xl">
                        {service.description}
                      </p>

                      <div className="mt-10 space-y-6">
                        {service.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="group flex items-start gap-5"
                          >
                            <div className="relative mt-2">
                              <div className="w-3 h-3 rounded-full bg-[#ee7d32]" />

                              <div className="absolute inset-0 rounded-full bg-[#ee7d32] animate-ping opacity-30" />
                            </div>

                            <p className="text-[#333] text-lg leading-8 transition duration-300 group-hover:text-[#111]">
                              {item}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full border border-[#ee7d32]/10" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA ANALISIS DE LABORATORIO */}
      <section className="relative py-14 md:py-20 bg-white overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] md:w-[500px] md:h-[500px] rounded-full pointer-events-none bg-[#ee7d32]/5 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[28px] md:rounded-[30px] border border-[#ececec] bg-white shadow-[0_10px_35px_rgba(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-orange-400 to-[#ee7d32]" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 px-5 py-7 sm:px-6 md:px-8 md:py-8 lg:px-10 lg:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-5 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />

                  <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center">
                    <Mail
                      size={34}
                      className="md:w-[42px] md:h-[42px] stroke-[#ee7d32] transition-colors duration-300"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <span className="inline-flex items-center rounded-full bg-[#ee7d32]/10 border border-[#ee7d32]/20 px-3 py-1 text-[11px] md:text-[12px] font-semibold uppercase tracking-wider text-[#ee7d32]">
                    Análisis y control especializado
                  </span>

                  <h3 className="mt-3 text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-[#111827] leading-tight">
                    ¿Necesitas análisis de laboratorio confiables para tu
                    operación?
                  </h3>

                  <p className="hidden md:block mt-3 text-sm md:text-base leading-6 md:leading-7 text-[#6b7280] max-w-2xl">
                    Realizamos análisis ambientales, físico-químicos y
                    microbiológicos con metodologías especializadas y resultados
                    confiables para la toma de decisiones técnicas y
                    regulatorias.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=ambiental&service=Análisis de laboratorio"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Solicitar análisis
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Laboratory;
